import AppLayout from '../layout/AppLayout';
import { useState, createRef, useEffect, useRef } from 'react';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import Cookies from 'js-cookie';
import axios from 'axios';
import regenerateToken from '../regenerateToken';
import './headerFooter.css'; // New CSS
import { useAlert } from 'react-alert';
import { buildRequestConfig } from '../../utils/apiHelper';
import { isAdmin as checkAdmin } from '../../utils/authUtils';
import { Spinner } from 'react-bootstrap';
import { FaCloudUploadAlt, FaCrop, FaSave, FaCheckCircle, FaEraser, FaFileImage, FaCog } from 'react-icons/fa';

const HeaderFooter = () => {
  const alert = useAlert();
  const [image, setImage] = useState('');
  const [cropData, setCropData] = useState({});
  const [category, setCategory] = useState(''); // 'header' | 'footer'
  const [margins, setMargins] = useState({});
  const [updateLoader, setUpdateLoader] = useState(false);

  const cropperRef = createRef();
  const fileInputRef = useRef(null);

  const onChange = e => {
    e.preventDefault();
    let files;
    if (e.dataTransfer) {
      files = e.dataTransfer.files;
    } else if (e.target) {
      files = e.target.files;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setImage(reader.result);
    };
    if (files && files[0]) {
      reader.readAsDataURL(files[0]);
    }
  };

  const getCropData = () => {
    if (typeof cropperRef.current?.cropper !== 'undefined') {
      cropperRef.current?.cropper.getCroppedCanvas().toBlob(function (blob) {
        if (!blob) {
          alert.error("Failed to crop image.");
          return;
        }
        if (Math.round(blob.size / 1024) < 150) {
          setCropData(prevData => ({
            ...prevData,
            [category]: cropperRef.current?.cropper
              .getCroppedCanvas()
              .toDataURL(),
          }));
          setMargins(prevData => ({
            ...prevData,
            [category]: cropperRef.current?.cropper.getCroppedCanvas().height,
          }));
          alert.success(`${category} cropped successfully!`);
        } else {
          alert.error(
            'File size exceeds 150KB limit. Please crop a smaller area or use a smaller image.'
          );
        }
      });
    }
  };

  const updatingHeaderFooter = async () => {
    const headerFooterBody = {
      headerBase64: cropData['header'] ? cropData['header'] : '',
      footerBase64: cropData['footer'] ? cropData['footer'] : '',
      headerMargin: margins['header'] ? margins['header'] + 'px' : '',
      footerMargin: margins['footer'] ? margins['footer'] + 'px' : '',
    };

    // ── ADMIN PATH: use admin token + inject clientId if a client is selected
    if (checkAdmin()) {
      return await axios(buildRequestConfig({
        method: 'POST',
        url: 'https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/assets/headerfooter',
        data: headerFooterBody,
      }));
    }
    // ── CLIENT PATH (unchanged) ──────────────────────────────────────────
    const idToken = Cookies.get('idToken');
    const configuration = {
      url: 'https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/assets/headerfooter',
      headers: {
        authorization: idToken,
      },
      method: 'POST',
      validateStatus: function (status) {
        return status >= 200 && status <= 401;
      },
      data: headerFooterBody,
    };
    const response = await axios(configuration);
    return response;
  };

  const handlingRequest = async () => {
    setUpdateLoader(true);
    try {
      const response = await updatingHeaderFooter();
      if (response.status === 401) {
        await regenerateToken();
        const response = await updatingHeaderFooter();
      }
      if (response.status >= 200 && response.status < 300) {
        alert.success("Changes published successfully!");
      } else {
        alert.error("Failed to publish changes.");
      }
    } catch (e) {
      console.log(e.message);
      alert.error("An error occurred while publishing.");
      setUpdateLoader(false);
    }
    setUpdateLoader(false);
  };

  const resetAll = () => {
    setImage('');
    setCategory('');
    setCropData({});
    setMargins({});
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <AppLayout>
      <div className='hf-container'>
        {/* Header */}
        <div className='hf-header'>
          <div className='hf-title-group'>
            <h1 className='hf-title'>Header & Footer Configuration</h1>
            <p className='hf-subtitle'>
              Manage your report letterhead, crop headers and footers, and publish changes.
            </p>
          </div>
          <div className='hf-actions-top'>
            <button
              className='hf-btn hf-btn-secondary'
              onClick={resetAll}
              disabled={!image && !category}
            >
              <FaEraser /> Reset
            </button>
            <button
              className='hf-btn hf-btn-primary'
              disabled={!(cropData['header'] && cropData['footer']) || updateLoader}
              onClick={handlingRequest}
            >
              {updateLoader ? (
                <Spinner animation='border' role='status' size='sm' />
              ) : (
                <>
                  <FaCheckCircle /> Confirm Changes
                </>
              )}
            </button>
          </div>
        </div>

        <div className='hf-grid'>
          {/* Left Column: Controls */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

            {/* 1. Upload Card */}
            <div className='hf-card'>
              <div className='hf-card-header'>
                <div className='hf-icon-box' style={{ background: '#eff6ff', color: '#2563eb' }}>
                  <FaCloudUploadAlt />
                </div>
                <h3 className='hf-card-title'>Upload Letterhead</h3>
              </div>
              <div className='hf-card-body'>
                <div className='hf-file-input-wrapper'>
                  <input
                    type='file'
                    className='hf-file-input'
                    ref={fileInputRef}
                    onChange={e => {
                      onChange(e);
                      // Original logic cleared these on change, preserving that
                      setCategory('');
                      setCropData({});
                      setMargins({});
                    }}
                    accept="image/*"
                  />
                  <div className='hf-file-drop-icon'><FaFileImage /></div>
                  <div className='hf-file-drop-text'>
                    {image ? 'Change File' : 'Click or Drag to Upload'}
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem' }}>
                  <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Max Size: 150KB (After Crop)</span>
                  {image && <span style={{ fontSize: '0.8rem', color: '#10b981', fontWeight: '500' }}>Image Loaded</span>}
                </div>
              </div>
            </div>

            {/* 2. Settings Card */}
            <div className='hf-card'>
              <div className='hf-card-header'>
                <div className='hf-icon-box' style={{ background: '#f0fdf4', color: '#16a34a' }}>
                  <FaCog />
                </div>
                <h3 className='hf-card-title'>Details</h3>
              </div>
              <div className='hf-card-body'>
                <div className='hf-input-group'>
                  <label className='hf-label'>Select Region</label>
                  <select
                    className='hf-select'
                    value={category}
                    onChange={e => setCategory(e.target.value)}
                    disabled={!image}
                  >
                    <option value="" hidden>Select Header or Footer</option>
                    <option value='header'>Header</option>
                    <option value='footer'>Footer</option>
                  </select>
                </div>

                {/* Margins Inputs */}
                <div className='hf-input-group'>
                  <label className='hf-label'>Header Margin (px)</label>
                  <input
                    type="number"
                    className='hf-input'
                    value={margins['header'] ?? ''}
                    onChange={(e) => setMargins(prev => ({ ...prev, header: e.target.value }))}
                    placeholder="Height/Margin for Header"
                  />
                </div>
                <div className='hf-input-group'>
                  <label className='hf-label'>Footer Margin (px)</label>
                  <input
                    type="number"
                    className='hf-input'
                    value={margins['footer'] ?? ''}
                    onChange={(e) => setMargins(prev => ({ ...prev, footer: e.target.value }))}
                    placeholder="Height/Margin for Footer"
                  />
                </div>

                <button
                  className='hf-btn hf-btn-success'
                  style={{ justifyContent: 'center', marginTop: '1rem' }}
                  onClick={() => getCropData()}
                  disabled={!image || !category}
                >
                  <FaCrop /> Crop {category ? category : 'Selection'}
                </button>
              </div>
            </div>

            {/* Preview Thumbnails */}
            {(cropData['header'] || cropData['footer']) && (
              <div className='hf-card'>
                <div className='hf-card-header'>
                  <div className='hf-icon-box' style={{ background: '#fff7ed', color: '#ea580c' }}>
                    <FaFileImage />
                  </div>
                  <h3 className='hf-card-title'>Current Crops</h3>
                </div>
                <div className='hf-card-body'>
                  {cropData['header'] && (
                    <div style={{ marginBottom: '1rem' }}>
                      <label className='hf-label'>Header Preview</label>
                      <img src={cropData['header']} alt="Header Crop" style={{ width: '100%', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                    </div>
                  )}
                  {cropData['footer'] && (
                    <div>
                      <label className='hf-label'>Footer Preview</label>
                      <img src={cropData['footer']} alt="Footer Crop" style={{ width: '100%', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                    </div>
                  )}
                </div>
              </div>
            )}

          </div>

          {/* Right Column: Editor */}
          <div className='hf-card' style={{ minHeight: '500px' }}>
            <div className='hf-card-header'>
              <div className='hf-icon-box' style={{ background: '#f5f3ff', color: '#7c3aed' }}>
                <FaCrop />
              </div>
              <h3 className='hf-card-title'>Editor Workspace</h3>
            </div>
            <div className='hf-card-body' style={{ justifyContent: 'center', alignItems: 'center', background: '#f8fafc' }}>
              {!image ? (
                <div style={{ textAlign: 'center', color: '#94a3b8' }}>
                  <FaCloudUploadAlt style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }} />
                  <p>Upload a letterhead image to start editing.</p>
                </div>
              ) : (
                <Cropper
                  ref={cropperRef}
                  style={{ width: '100%', height: '500px' }}
                  // zoomTo={0}
                  initialAspectRatio={1}
                  preview='.img-preview'
                  src={image}
                  viewMode={1}
                  minCropBoxHeight={10}
                  minCropBoxWidth={10}
                  background={false}
                  responsive={true}
                  autoCropArea={1}
                  checkOrientation={false} // https://github.com/fengyuanchen/cropperjs/issues/671
                  guides={true}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default HeaderFooter;
